import { Heading, Box, Paragraph } from 'grommet'
import styled, { css } from 'styled-components'

import { SquareButton } from '@ui/buttons/SquareButton'

import {
  ArticleBlockParagraphProps,
  ArticleBlockTitleProps,
  EditableBlock,
} from './types'

export const Wrapper = styled(Box)`
  ${({ theme }) => css`
    color: ${theme.newColors.darkGray};
    margin: ${theme.newSpaces.xl5}px auto ${theme.newSpaces.xl3}px;
    width: 657px;

    ${theme.above.laptopL`
      ${theme.getTypography('desktop', 'body', 'body1')}
    `}

    ${theme.below.laptopL`
      width: 657px;
    `}

    img {
      margin: 0 auto;
      width: auto;
    }

    ~ form {
      margin-top: 0px;
    }

    ${theme.below.laptop`
      padding: ${theme.newSpaces.xs2}px;
      margin: ${theme.newSpaces.xl3}px auto;
    `}

    ${theme.below.mobileXL`
      padding: ${theme.newSpaces.xs}px;
      margin: ${theme.newSpaces.lg}px auto 30px;
    `}
  `}
`

export const ArticleBlockTitle = styled(Heading)<ArticleBlockTitleProps>`
  ${({ theme, textColor = 'gray', marginTop, paddingLeft }) => css`
    ${theme.getTypography('desktop', 'title', 'title1')};

    align-self: flex-start;
    color: ${textColor === 'inherit' ? textColor : theme.newColors[textColor]};
    margin-top: ${marginTop || '50px'};
    padding-left: ${paddingLeft || '0px'};
    word-wrap: break-word;

    ${theme.below.mobileL`
      ${theme.getTypography('desktop', 'title', 'title1')};

      margin-top: ${marginTop || theme.newSpaces.xl2}px;
    `}

    ${theme.below.mobileS`
      ${theme.getTypography('desktop', 'title', 'title2')};
    `}
  `}
`

export const ArticleBlockImage = styled(Box)<EditableBlock>`
  ${({ theme, marginTop, paddingLeft }) => css`
    margin: 0 auto;
    max-width: 657px;
    min-height: 248px;
    padding-left: ${paddingLeft || '0px'};
    position: relative;
    width: 100%;

    img {
      height: auto;
      object-fit: cover;
      width: 100%;
    }

    ${theme.above.laptopL`
      max-width: 100%;
      margin-top: 40px;
    `}

    ${theme.below.laptopL`
      margin-top: ${marginTop || '30px'};
    `}

    ${theme.below.tablet`
      min-height: 220px;
   `}

    ${theme.below.mobileXL`
      margin-top: ${theme.newSpaces.xs}px;
   `}

    ${theme.below.mobileL`
      min-height: 150px;
    `}
  `}
`

export const ArticleBlockText = styled.span<EditableBlock>`
  ${({ theme, marginTop, paddingLeft }) => css`
    ${theme.getTypography('desktop', 'body', 'body3')};

    padding-left: ${paddingLeft || '0px'};
    width: 100%;
    word-wrap: break-word;

    ${theme.above.laptopL`
      ${theme.getTypography('desktop', 'body', 'body3')};

      margin-top: 40px;
    `}

    ${theme.below.laptopL`
      margin-top: ${marginTop || '20px'};
    `}
  `}
`

export const ArticleBlockParagraph = styled(
  Paragraph
)<ArticleBlockParagraphProps>`
  text-align: justify;
  white-space: pre-wrap;

  ${({
    theme,
    marginTop,
    mobileMarginTop,
    paddingLeft,
    alignSelf,
    maxWidth,
    fontSize = 'body3',
  }) => css`
    align-self: ${alignSelf || 'normal'};
    ${theme.getTypography('desktop', 'body', fontSize)};
    margin-top: ${marginTop || '20px'};
    max-width: ${maxWidth || 100}%;
    padding-left: ${paddingLeft || '0px'};

    ${theme.below.tablet`
      margin-top: 0px;

      &:nth-child(n+2) {
        margin-top: ${mobileMarginTop ?? `${theme.newSpaces.xs}px`};
      }
    `}
  `}
`

export const Quote = styled('q')<EditableBlock>`
  ${({ theme, marginTop, paddingLeft, fontSize, fontWeight }) => css`
    margin: 0 auto;

    margin-top: ${marginTop || '52px'};
    max-width: 657px;
    padding-left: ${paddingLeft || '0px'};
    width: 100%;

    & > blockquote {
      font-weight: ${fontWeight || 300};
      ${theme.getTypography('desktop', 'body', fontSize || 'body3')};

      color: ${theme.newColors.gray};
      margin: 25px auto 50px auto;
      max-width: 489px;
      text-align: center;
    }

    &::before {
      color: ${theme.newColors.accent};
      float: left;
      font-size: ${theme.fontSizes[10]}px;
    }

    &::after {
      color: ${theme.newColors.accent};
      float: right;
      font-size: ${theme.fontSizes[10]}px;
    }
  `}
`

export const Button = styled(SquareButton)`
  text-decoration-thickness: 2px;
  text-underline-offset: 6px;

  &:hover {
    text-decoration-line: underline;
    text-decoration-thickness: 2px;
    text-underline-offset: 6px;
  }
`

export const TextWrapper = styled(Box)`
  justify-content: center;
  left: 5%;
  position: absolute;
  top: 12%;
  width: 382px;

  ${({ theme }) => css`
    ${theme.above.desktopS`
      top: 25%;
    `}

    ${theme.below.tablet`
      top: unset;
      bottom: ${theme.newSpaces.xs3}%;
    `}

    ${theme.below.mobileXL`
      width: 278px;
    `}
  `}
`

export const Header = styled(Paragraph)`
  ${({ theme }) => css`
    color: ${theme.newColors.primary};
    font-size: 32px;
    font-style: normal;
    font-weight: ${theme.typography.desktop.title.title4.fontWeight}px;
    line-height: ${theme.typography.mobile.title.blog.lineHeight}px;
    white-space: pre-wrap;

    ${theme.below.tabletS`
      ${theme.getTypography('mobile', 'title', 'title3')}

      text-transform: uppercase;
    `}
  `}
`

export const Subtitle = styled(Paragraph)`
  ${({ theme }) => css`
    ${theme.getTypography('desktop', 'body', 'body3')}

    margin-bottom: ${theme.newSpaces.lg}px;
    margin-top: ${theme.newSpaces.xs}px;
    max-width: 100%;

    ${theme.below.tablet`
      margin-top: ${theme.newSpaces.xs2}px;
      margin-bottom: ${theme.newSpaces.xs}px;
    `}

    ${theme.below.tabletS`
      ${theme.getTypography('mobile', 'body', 'body4')}
    `}
  `}
`
export const ImageContainer = styled(Box)`
  ${({ theme }) => css`
    margin: 0 auto;
    margin-top: ${theme.newSpaces.lg}px;

    ${theme.below.mobileXL`
      width: 100%;
      height: 100%;
      margin-top: ${theme.newSpaces.xs}px;

      & > img {
        height: 100%;
        width: 100%;
      }
    `}
  `}
`
